import React, { useEffect, useState, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { ExportButton } from '../components/ExportButton';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Plus,
  Trash2,
  Eye,
  Search,
  TrendingUp,
  TrendingDown,
  Minus,
  Users,
  Filter,
  Copy,
  MessageCircle,
  QrCode,
  Download,
  Network,
  Info,
  Sparkles,
  Star,
  AlertTriangle,
  Target,
  Link2,
  Share2,
  Check,
  Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { PhoneInput } from '../components/PhoneInput';
import { StateCitySelect, ESTADOS } from '../components/StateCitySelect';
import { useSettings } from '../hooks/useSettings';
import { usePermissions } from '../hooks/usePermissions';
import { useQrCode } from '../hooks/useQrCode';
import { useAuth } from '../contexts/AuthContext';
import { NetworkGraph } from '../components/NetworkGraph';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { ImageUpload } from '../components/ImageUpload';
import { LeaderDetailsDrawer } from '../components/LeaderDetailsDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const LiderancasPage = () => {
  const { settings } = useSettings();
  const { canEdit } = usePermissions();
  const { activeTenant } = useAuth();
  const canEditLiderancas = canEdit('liderancas');
  const tiposLideranca = settings?.lideranca_tipos || [];
  const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const buildInviteUrl = (token) =>
    token && activeTenant?.slug ? `${apiBase}/api/og/r/${activeTenant.slug}/${token}` : '';
  const publicLeaderSignupUrl = activeTenant?.slug
    ? `${apiBase}/api/og/cadastrar/${activeTenant.slug}`
    : '';
  const publicLeaderSignupQr = useQrCode(publicLeaderSignupUrl);
  const [publicSignupCopied, setPublicSignupCopied] = useState(false);
  const [publicSignupPopoverOpen, setPublicSignupPopoverOpen] = useState(false);
  const leaderSignupEnabled = settings?.leader_signup_enabled !== false;

  const copyPublicSignup = async () => {
    if (!publicLeaderSignupUrl) return;
    try {
      await navigator.clipboard.writeText(publicLeaderSignupUrl);
      setPublicSignupCopied(true);
      toast.success('Link público copiado!');
      setTimeout(() => setPublicSignupCopied(false), 2500);
    } catch (_) {
      toast.error('Não foi possível copiar.');
    }
  };

  const downloadPublicSignupQr = () => {
    if (!publicLeaderSignupQr) return;
    const a = document.createElement('a');
    a.href = publicLeaderSignupQr;
    a.download = `qr-cadastro-lideranca-${activeTenant?.slug || 'campanha'}.png`;
    a.click();
  };

  // ===== Per-leader capture meta override (consolidated into LeaderDetailsDrawer) =====
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLeader, setDrawerLeader] = useState(null);
  const [drawerInitialTab, setDrawerInitialTab] = useState('performance');

  const openLeaderDrawer = (lider, tab = 'performance') => {
    setDrawerLeader(lider);
    setDrawerInitialTab(tab);
    setDrawerOpen(true);
  };
  const [productivity, setProductivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedLideranca, setSelectedLideranca] = useState(null);
  const [inviteLideranca, setInviteLideranca] = useState(null);
  const [editingLeaderId, setEditingLeaderId] = useState(null);
  const inviteUrl = buildInviteUrl(inviteLideranca?.primary_link_token);
  const inviteQr = useQrCode(inviteUrl);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterCidade, setFilterCidade] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDobrada, setFilterDobrada] = useState('');
  const [dobradas, setDobradas] = useState([]);

  const [formData, setFormData] = useState({
    nome_completo: '',
    apelido: '',
    telefone: '',
    data_nascimento: '',
    estado: '',
    cidade: '',
    bairro: '',
    tipo: 'comunitária',
    status: 'ativo',
    observacoes: '',
    dobrada_id: '',
  });

  // Network tab
  const [networkData, setNetworkData] = useState({ nodes: [], links: [] });
  const [networkMetrics, setNetworkMetrics] = useState({});
  const [networkInsights, setNetworkInsights] = useState([]);
  const [networkTopCentral, setNetworkTopCentral] = useState([]);
  const [networkLoading, setNetworkLoading] = useState(false);
  const [networkFilter, setNetworkFilter] = useState({ search: '', estado: '', cidade: '', tipo: '' });
  const [networkLoaded, setNetworkLoaded] = useState(false);

  useEffect(() => {
    fetchData();
    fetchDobradas();
  }, []);

  const fetchData = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/stats/leaders-productivity`, {
        withCredentials: true,
      });
      setProductivity(data);
    } catch (error) {
      toast.error('Erro ao carregar lideranças');
    } finally {
      setLoading(false);
    }
  };

  const fetchDobradas = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/dobradas`, {
        withCredentials: true,
      });
      setDobradas(data || []);
    } catch (err) {
      // Permissões ausentes podem retornar 403; silenciar para não quebrar a página
    }
  };

  const openEditLeaderDialog = async (lider) => {
    // `lider` may come from the productivity array (limited shape) or from
    // the leaders endpoint. Always pull the full record so we can pre-fill
    // every editable field accurately.
    const leaderId = lider.lideranca_id || lider.id;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/leaders/${leaderId}`, {
        withCredentials: true,
      });
      setEditingLeaderId(data.id);
      setFormData({
        nome_completo: data.nome_completo || '',
        apelido: data.apelido || '',
        telefone: data.telefone || '',
        data_nascimento: data.data_nascimento || '',
        estado: data.estado || '',
        cidade: data.cidade || '',
        bairro: data.bairro || '',
        tipo: data.tipo || 'comunitária',
        status: data.status || 'ativo',
        observacoes: data.observacoes || '',
        dobrada_id: data.dobrada_id || '',
      });
      setDialogOpen(true);
    } catch (err) {
      toast.error('Não foi possível carregar a liderança para edição');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.estado) delete payload.estado;
      if (!payload.bairro) delete payload.bairro;
      if (!payload.observacoes) delete payload.observacoes;
      if (!payload.dobrada_id) payload.dobrada_id = null;
      if (editingLeaderId) {
        await axios.put(`${BACKEND_URL}/api/leaders/${editingLeaderId}`, payload, {
          withCredentials: true,
        });
        toast.success('Liderança atualizada com sucesso!');
      } else {
        await axios.post(`${BACKEND_URL}/api/leaders`, payload, {
          withCredentials: true,
        });
        toast.success('Liderança criada com sucesso!');
      }
      setDialogOpen(false);
      setEditingLeaderId(null);
      resetForm();
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Erro ao salvar liderança';
      toast.error(message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta liderança?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/leaders/${id}`, {
        withCredentials: true,
      });
      toast.success('Liderança excluída com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir liderança');
    }
  };

  const handleViewDetails = async (lideranca) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/leaders/${lideranca.lideranca_id}`, {
        withCredentials: true,
      });
      setSelectedLideranca(data);
      setDetailsDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleCopyInvite = async (lideranca) => {
    const url = buildInviteUrl(lideranca.primary_link_token);
    if (!url) {
      toast.error('Esta liderança ainda não tem link de convite. Crie um link em "Links".');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link de convite copiado!');
    } catch {
      toast.error('Não foi possível copiar o link');
    }
  };

  const handleShareWhatsApp = (lideranca) => {
    const url = buildInviteUrl(lideranca.primary_link_token);
    if (!url) {
      toast.error('Esta liderança ainda não tem link de convite.');
      return;
    }
    const text = `Olá! ${lideranca.apelido || lideranca.nome || 'sua liderança'} te convidou para entrar na nossa rede. Acesse: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleOpenInvite = (lideranca) => {
    if (!lideranca.primary_link_token) {
      toast.error('Esta liderança ainda não tem link de convite.');
      return;
    }
    setInviteLideranca(lideranca);
  };

  const downloadInviteQr = () => {
    if (!inviteQr) return;
    const a = document.createElement('a');
    a.href = inviteQr;
    a.download = `convite-${inviteLideranca?.apelido || 'lideranca'}.png`;
    a.click();
  };

  const fetchNetwork = async () => {
    setNetworkLoading(true);
    try {
      const params = {};
      if (filterDobrada) {
        params.dobrada_id = filterDobrada === 'principal' ? '__principal__' : filterDobrada;
      }
      const { data } = await axios.get(`${BACKEND_URL}/api/stats/network`, {
        withCredentials: true,
        params,
      });
      setNetworkData({ nodes: data.nodes || [], links: data.links || [] });
      setNetworkMetrics(data.metrics || {});
      setNetworkInsights(data.insights || []);
      setNetworkTopCentral(data.top_centralidade || []);
      setNetworkLoaded(true);
    } catch (err) {
      toast.error('Erro ao carregar a rede');
    } finally {
      setNetworkLoading(false);
    }
  };

  // Refetch the network graph when the dobrada filter changes (only if the tab is already loaded)
  useEffect(() => {
    if (networkLoaded) {
      fetchNetwork();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDobrada]);

  const resetForm = () => {
    setFormData({
      nome_completo: '',
      apelido: '',
      telefone: '',
      estado: '',
      cidade: '',
      bairro: '',
      tipo: 'comunitária',
      status: 'ativo',
      observacoes: '',
      dobrada_id: '',
    });
  };

  // Apply filters
  const filtered = useMemo(() => {
    return productivity.filter((l) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matches =
          l.nome.toLowerCase().includes(term) ||
          (l.apelido || '').toLowerCase().includes(term) ||
          l.cidade.toLowerCase().includes(term);
        if (!matches) return false;
      }
      if (filterEstado && filterEstado !== 'todos' && l.estado !== filterEstado) return false;
      if (filterCidade && filterCidade !== 'todas' && l.cidade !== filterCidade) return false;
      if (filterTipo && filterTipo !== 'todos' && l.tipo !== filterTipo) return false;
      if (filterStatus && filterStatus !== 'todos' && l.status !== filterStatus) return false;
      if (filterDobrada) {
        if (filterDobrada === 'principal') {
          if (l.dobrada_id) return false;
        } else if (filterDobrada !== 'todas' && l.dobrada_id !== filterDobrada) {
          return false;
        }
      }
      return true;
    });
  }, [productivity, searchTerm, filterEstado, filterCidade, filterTipo, filterStatus, filterDobrada]);

  // Cities available in current data filtered by estado
  const availableCities = useMemo(() => {
    const cities = new Set(
      productivity
        .filter((l) => !filterEstado || filterEstado === 'todos' || l.estado === filterEstado)
        .map((l) => l.cidade)
        .filter(Boolean)
    );
    return Array.from(cities).sort();
  }, [productivity, filterEstado]);

  // KPIs based on filtered data
  const kpis = useMemo(() => {
    const total = filtered.length;
    const ativos = filtered.filter((l) => l.status === 'ativo').length;
    const totalAcessos = filtered.reduce((acc, l) => acc + l.total_acessos, 0);
    const totalRedirects = filtered.reduce((acc, l) => acc + l.total_redirecionamentos, 0);
    const totalSupporters = filtered.reduce((acc, l) => acc + l.supporters_identificados, 0);
    const acessos7d = filtered.reduce((acc, l) => acc + l.acessos_7d, 0);
    return { total, ativos, totalAcessos, totalRedirects, totalSupporters, acessos7d };
  }, [filtered]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterEstado('');
    setFilterCidade('');
    setFilterTipo('');
    setFilterStatus('');
    setFilterDobrada('');
  };

  const renderTrendIcon = (tendencia) => {
    if (tendencia > 5) return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (tendencia < -5) return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="liderancas-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black tracking-tight">Lideranças</h1>
            <p className="text-muted-foreground mt-2">
              Gestão e análise de produtividade da rede
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton resource="leaders" />
            {leaderSignupEnabled && publicLeaderSignupUrl && (
              <Popover open={publicSignupPopoverOpen} onOpenChange={setPublicSignupPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="gap-2"
                    data-testid="public-leader-invite-btn"
                  >
                    <Share2 className="h-4 w-4" />
                    Link público de convite
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[380px] p-5" data-testid="public-leader-invite-popover">
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <h3 className="text-base font-bold text-slate-900">Convidar novas lideranças</h3>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Compartilhe este link com pessoas que ainda <strong>não estão cadastradas</strong>. Elas vão entrar na rede e receber automaticamente um link personalizado.
                      </p>
                    </div>

                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 flex items-center gap-2">
                      <code
                        className="text-xs text-slate-700 font-mono break-all flex-1"
                        data-testid="public-leader-invite-url"
                      >
                        {publicLeaderSignupUrl.replace(/^https?:\/\//, '')}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={copyPublicSignup}
                        className="h-7 w-7 p-0 shrink-0"
                        data-testid="public-leader-invite-copy"
                        title="Copiar link"
                      >
                        {publicSignupCopied ? (
                          <Check className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <Copy className="h-4 w-4 text-slate-500" />
                        )}
                      </Button>
                    </div>

                    {publicLeaderSignupQr && (
                      <div className="flex items-center gap-3 rounded-md border border-slate-200 p-3">
                        <img
                          src={publicLeaderSignupQr}
                          alt="QR Code do link público"
                          className="h-20 w-20 rounded border border-slate-100"
                          data-testid="public-leader-invite-qr"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-slate-700">QR Code</div>
                          <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
                            Imprima em cartazes, panfletos ou flyers para captação offline.
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2 h-7 gap-1.5 text-[11px]"
                            onClick={downloadPublicSignupQr}
                            data-testid="public-leader-invite-qr-download"
                          >
                            <Download className="h-3 w-3" />
                            Baixar
                          </Button>
                        </div>
                      </div>
                    )}

                    <Button
                      asChild
                      size="sm"
                      variant="outline"
                      className="w-full gap-2"
                      data-testid="public-leader-invite-open"
                    >
                      <a href={publicLeaderSignupUrl} target="_blank" rel="noopener noreferrer">
                        Abrir página de cadastro
                        <Eye className="h-3.5 w-3.5" />
                      </a>
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            )}
            <Button onClick={() => { setEditingLeaderId(null); resetForm(); setDialogOpen(true); }} data-testid="create-lideranca-btn" disabled={!canEditLiderancas}>
              <Plus className="h-4 w-4 mr-2" />
              Nova Liderança
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-6">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
              <p className="text-3xl font-bold mt-1">{kpis.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Ativas</p>
              <p className="text-3xl font-bold mt-1 text-green-600">{kpis.ativos}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Acessos</p>
              <p className="text-3xl font-bold mt-1 text-primary">{kpis.totalAcessos}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Redirects</p>
              <p className="text-3xl font-bold mt-1 text-primary">{kpis.totalRedirects}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Apoiadores</p>
              <p className="text-3xl font-bold mt-1 text-primary">{kpis.totalSupporters}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Últ. 7 dias</p>
              <p className="text-3xl font-bold mt-1 text-primary">{kpis.acessos7d}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="font-bold text-sm">Filtros</span>
              {(searchTerm || filterEstado || filterCidade || filterTipo || filterStatus || filterDobrada) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="ml-auto"
                  data-testid="clear-filters-btn"
                >
                  Limpar filtros
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="relative lg:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome, apelido ou cidade..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-liderancas"
                />
              </div>
              <Select
                value={filterEstado || 'todos'}
                onValueChange={(v) => {
                  setFilterEstado(v === 'todos' ? '' : v);
                  setFilterCidade('');
                }}
              >
                <SelectTrigger data-testid="filter-estado">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos estados</SelectItem>
                  {ESTADOS.map((e) => (
                    <SelectItem key={e.sigla} value={e.sigla}>
                      {e.sigla} - {e.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filterCidade || 'todas'}
                onValueChange={(v) => setFilterCidade(v === 'todas' ? '' : v)}
              >
                <SelectTrigger data-testid="filter-cidade">
                  <SelectValue placeholder="Cidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas cidades</SelectItem>
                  {availableCities.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filterTipo || 'todos'}
                onValueChange={(v) => setFilterTipo(v === 'todos' ? '' : v)}
              >
                <SelectTrigger data-testid="filter-tipo">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos tipos</SelectItem>
                  {tiposLideranca.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dobradas.length > 0 && (
                <Select
                  value={filterDobrada || 'todas'}
                  onValueChange={(v) => setFilterDobrada(v === 'todas' ? '' : v)}
                >
                  <SelectTrigger data-testid="filter-dobrada">
                    <SelectValue placeholder="Dobrada" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas dobradas</SelectItem>
                    <SelectItem value="principal">Candidato principal</SelectItem>
                    {dobradas.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* List Views */}
        <Tabs defaultValue="produtividade" className="w-full" onValueChange={(v) => {
          if (v === 'rede' && !networkLoaded) fetchNetwork();
        }}>
          <TabsList>
            <TabsTrigger value="produtividade" data-testid="tab-produtividade">
              Produtividade
            </TabsTrigger>
            <TabsTrigger value="cards" data-testid="tab-cards">
              Cards
            </TabsTrigger>
            <TabsTrigger value="rede" data-testid="tab-rede">
              <Network className="h-3.5 w-3.5 mr-1.5" />
              Rede
            </TabsTrigger>
          </TabsList>

          {/* Productivity Table */}
          <TabsContent value="produtividade" className="space-y-3 mt-4">
            {filtered.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">
                    {productivity.length === 0
                      ? 'Nenhuma liderança cadastrada ainda.'
                      : 'Nenhuma liderança encontrada com os filtros aplicados.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-border bg-secondary/30">
                      <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="px-4 py-3 font-bold">#</th>
                        <th className="px-4 py-3 font-bold">Liderança</th>
                        <th className="px-4 py-3 font-bold">Localidade</th>
                        <th className="px-4 py-3 font-bold text-right">Acessos</th>
                        <th className="px-4 py-3 font-bold text-right">Redirects</th>
                        <th className="px-4 py-3 font-bold text-right">Apoiadores</th>
                        <th className="px-4 py-3 font-bold text-right">Taxa</th>
                        <th className="px-4 py-3 font-bold text-right">7d</th>
                        <th className="px-4 py-3 font-bold text-center">Tend.</th>
                        <th className="px-4 py-3 font-bold text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((l, idx) => (
                        <tr
                          key={l.lideranca_id}
                          className="border-b border-border hover:bg-emerald-50/40 transition-colors cursor-pointer"
                          data-testid={`lideranca-row-${l.lideranca_id}`}
                          onClick={(e) => {
                            // Ignore clicks on interactive elements inside the row
                            if (e.target.closest('button, a, input, [role="button"]')) return;
                            openLeaderDrawer(l, 'performance');
                          }}
                        >
                          <td className="px-4 py-3 font-bold text-muted-foreground">
                            #{idx + 1}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{l.nome}</div>
                            <div className="text-xs text-muted-foreground">
                              {l.apelido ? `@${l.apelido} • ${l.tipo}` : l.tipo}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {l.estado && (
                              <span className="font-bold text-primary mr-1">{l.estado}</span>
                            )}
                            {l.cidade}
                            <div className="text-xs text-muted-foreground">{l.bairro}</div>
                          </td>
                          <td className="px-4 py-3 text-right font-bold">{l.total_acessos}</td>
                          <td className="px-4 py-3 text-right font-bold text-primary">
                            {l.total_redirecionamentos}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-primary">
                            {l.supporters_identificados}
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {l.taxa_conversao}%
                          </td>
                          <td className="px-4 py-3 text-right font-medium">{l.acessos_7d}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              {renderTrendIcon(l.tendencia_7d)}
                              <span
                                className={`text-xs font-bold ${
                                  l.tendencia_7d > 5
                                    ? 'text-green-600'
                                    : l.tendencia_7d < -5
                                    ? 'text-red-600'
                                    : 'text-muted-foreground'
                                }`}
                              >
                                {l.tendencia_7d > 0 ? '+' : ''}
                                {l.tendencia_7d}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCopyInvite(l)}
                                disabled={!l.primary_link_token}
                                title="Copiar link de convite"
                                data-testid={`copy-invite-${l.lideranca_id}`}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenInvite(l)}
                                disabled={!l.primary_link_token}
                                title="Ver QR Code do convite"
                                data-testid={`view-invite-${l.lideranca_id}`}
                              >
                                <QrCode className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openLeaderDrawer(l, 'performance')}
                                title="Ver detalhes (performance, links, captação, apoiadores)"
                                data-testid={`view-lideranca-${l.lideranca_id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openLeaderDrawer(l, 'captacao')}
                                disabled={!canEditLiderancas}
                                title={
                                  (l.capture_hero_image_url || l.capture_subtitle)
                                    ? 'Personalização ativa — clique para editar'
                                    : 'Personalizar página de captação desta liderança'
                                }
                                className={(l.capture_hero_image_url || l.capture_subtitle) ? 'text-emerald-600 hover:text-emerald-700' : ''}
                                data-testid={`capture-meta-${l.lideranca_id}`}
                              >
                                <Sparkles className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditLeaderDialog(l)}
                                disabled={!canEditLiderancas}
                                title="Editar dados da liderança"
                                data-testid={`edit-lideranca-${l.lideranca_id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(l.lideranca_id)}
                                data-testid={`delete-lideranca-${l.lideranca_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* Cards View */}
          <TabsContent value="cards" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((l) => (
                <Card key={l.lideranca_id} data-testid={`lideranca-card-${l.lideranca_id}`}>
                  <CardHeader>
                    <CardTitle className="text-lg">{l.nome}</CardTitle>
                    <CardDescription>
                      {l.apelido && <>@{l.apelido} • </>}
                      {l.estado && `${l.estado}/`}
                      {l.cidade}{l.bairro && `/${l.bairro}`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Tipo:</span>
                      <span className="font-medium capitalize">{l.tipo}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Acessos:</span>
                      <span className="font-bold text-primary">{l.total_acessos}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Redirects:</span>
                      <span className="font-bold text-primary">{l.total_redirecionamentos}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Apoiadores:</span>
                      <span className="font-bold text-primary">{l.supporters_identificados}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Tendência 7d:</span>
                      <div className="flex items-center gap-1">
                        {renderTrendIcon(l.tendencia_7d)}
                        <span
                          className={`font-bold ${
                            l.tendencia_7d > 5
                              ? 'text-green-600'
                              : l.tendencia_7d < -5
                              ? 'text-red-600'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {l.tendencia_7d > 0 ? '+' : ''}
                          {l.tendencia_7d}%
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleCopyInvite(l)}
                        disabled={!l.primary_link_token}
                        data-testid={`card-copy-invite-${l.lideranca_id}`}
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Copiar convite
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenInvite(l)}
                        disabled={!l.primary_link_token}
                        title="QR Code"
                      >
                        <QrCode className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openLeaderDrawer(l, 'performance')}
                        title="Ver detalhes"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditLeaderDialog(l)}
                        disabled={!canEditLiderancas}
                        title="Editar liderança"
                        data-testid={`edit-card-${l.lideranca_id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(l.lideranca_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Network Graph */}
          <TabsContent value="rede" className="mt-4">
            {networkLoading ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="h-[560px] rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-500">
                    Carregando rede...
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
                {/* LEFT: Network overview */}
                <div className="xl:col-span-3 space-y-4">
                  <Card>
                    <CardContent className="pt-6 space-y-4">
                      <div>
                        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Visão geral</p>
                        <p className="text-2xl font-bold mt-1" data-testid="network-total-contatos">
                          {networkMetrics.total_contatos || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">Total de contatos</p>
                      </div>
                      <div className="space-y-3 pt-2 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full bg-[#0D1B3D]"></span>
                            <span className="text-sm text-slate-700">Lideranças</span>
                          </div>
                          <span className="text-sm font-bold">{networkMetrics.total_liderancas || 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full bg-[#3B82F6]"></span>
                            <span className="text-sm text-slate-700">Apoiadores</span>
                          </div>
                          <span className="text-sm font-bold">{networkMetrics.total_apoiadores || 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-700">Conexões totais</span>
                          <span className="text-sm font-bold">{networkMetrics.total_conexoes || 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-700">Grupos/Destinos</span>
                          <span className="text-sm font-bold">{networkMetrics.total_destinos || 0}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                        <div>
                          <p className="text-xs text-muted-foreground">Densidade</p>
                          <div className="flex items-baseline gap-2">
                            <span className="text-lg font-bold">{networkMetrics.densidade?.toFixed(2) || '0,00'}</span>
                            <Badge
                              variant="outline"
                              className={
                                networkMetrics.densidade_label === 'Alta'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]'
                                  : networkMetrics.densidade_label === 'Média'
                                  ? 'bg-amber-50 text-amber-700 border-amber-200 text-[10px]'
                                  : 'bg-slate-100 text-slate-600 border-slate-200 text-[10px]'
                              }
                            >
                              {networkMetrics.densidade_label || '—'}
                            </Badge>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Diâmetro</p>
                          <p className="text-lg font-bold">{networkMetrics.diametro || 0}</p>
                          <p className="text-[10px] text-muted-foreground">níveis</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Legend */}
                  <Card>
                    <CardContent className="pt-6 space-y-2.5">
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Legenda</p>
                      <div className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full bg-[#0D1B3D] ring-2 ring-[#22C55E]"></span>
                        <span className="text-xs text-slate-700">Liderança</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full bg-[#22C55E]"></span>
                        <span className="text-xs text-slate-700">Apoiador forte (3+ toques)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full bg-[#3B82F6]"></span>
                        <span className="text-xs text-slate-700">Apoiador (2 toques)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full bg-[#CBD5E1]"></span>
                        <span className="text-xs text-slate-700">Contato fraco</span>
                      </div>
                      <div className="pt-2 border-t border-slate-100 space-y-1.5 mt-2">
                        <div className="flex items-center gap-2">
                          <span className="h-0.5 w-6 bg-slate-700"></span>
                          <span className="text-xs text-slate-700">Atribuição original</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="h-0.5 w-6 border-t border-dashed border-emerald-500"></span>
                          <span className="text-xs text-slate-700">Toque adicional</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* CENTER: Graph */}
                <div className="xl:col-span-6">
                  <Card>
                    <CardContent className="pt-6 space-y-3">
                      <div>
                        <h3 className="text-lg font-bold tracking-tight">Mapa de Relacionamentos</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          Arraste para mover · Role para zoom · Clique nos nós para focar
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                          <Input
                            placeholder="Buscar por nome..."
                            value={networkFilter.search}
                            onChange={(e) => setNetworkFilter({ ...networkFilter, search: e.target.value })}
                            className="pl-8 h-9 text-sm"
                            data-testid="network-search-input"
                          />
                        </div>
                        <Select
                          value={networkFilter.estado || 'all'}
                          onValueChange={(v) =>
                            setNetworkFilter({ ...networkFilter, estado: v === 'all' ? '' : v, cidade: '' })
                          }
                        >
                          <SelectTrigger className="h-9 text-sm" data-testid="network-estado-filter">
                            <SelectValue placeholder="Estado" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos os estados</SelectItem>
                            {ESTADOS.map((e) => (
                              <SelectItem key={e.sigla} value={e.sigla}>
                                {e.sigla} — {e.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Cidade"
                          value={networkFilter.cidade}
                          onChange={(e) => setNetworkFilter({ ...networkFilter, cidade: e.target.value })}
                          className="h-9 text-sm"
                          data-testid="network-cidade-filter"
                        />
                        <Select
                          value={networkFilter.tipo || 'all'}
                          onValueChange={(v) =>
                            setNetworkFilter({ ...networkFilter, tipo: v === 'all' ? '' : v })
                          }
                        >
                          <SelectTrigger className="h-9 text-sm" data-testid="network-tipo-filter">
                            <SelectValue placeholder="Tipo" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="leader">Apenas lideranças</SelectItem>
                            <SelectItem value="supporter">Apenas apoiadores</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {networkData.nodes.length === 0 ? (
                        <div className="h-[460px] rounded-xl border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-center text-sm text-slate-500 px-6">
                          Ainda não há lideranças ou apoiadores cadastrados para formar a rede.
                        </div>
                      ) : (
                        <NetworkGraph
                          nodes={networkData.nodes}
                          links={networkData.links}
                          filter={networkFilter}
                          height={520}
                        />
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* RIGHT: Insights + Top centralidade */}
                <div className="xl:col-span-3 space-y-4">
                  <Card>
                    <CardContent className="pt-6 space-y-3">
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                        Insights da rede
                      </p>
                      {networkInsights.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Sem insights por enquanto.</p>
                      ) : (
                        networkInsights.map((ins, idx) => {
                          const iconCls =
                            ins.type === 'warning'
                              ? 'bg-amber-100 text-amber-700'
                              : ins.type === 'highlight'
                              ? 'bg-emerald-100 text-emerald-700'
                              : ins.type === 'star'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-slate-100 text-slate-700';
                          const Icon =
                            ins.type === 'warning'
                              ? AlertTriangle
                              : ins.type === 'highlight'
                              ? Sparkles
                              : ins.type === 'star'
                              ? Star
                              : Info;
                          return (
                            <div key={idx} className="flex gap-2.5 items-start">
                              <div className={`h-7 w-7 rounded-lg ${iconCls} flex items-center justify-center shrink-0`}>
                                <Icon className="h-3.5 w-3.5" />
                              </div>
                              <p className="text-sm text-slate-700 leading-snug">{ins.text}</p>
                            </div>
                          );
                        })
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6 space-y-3">
                      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                        Top apoiadores (centralidade)
                      </p>
                      {networkTopCentral.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          Quando houver apoiadores conectados a múltiplas lideranças, eles aparecerão aqui.
                        </p>
                      ) : (
                        networkTopCentral.map((c, idx) => (
                          <div key={c.id} className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center text-xs font-bold text-emerald-700 shrink-0">
                              {idx + 1}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-slate-900 truncate">
                                {c.name || '—'}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {c.cidade || 'Sem cidade'}
                              </p>
                            </div>
                            <span className="text-sm font-bold text-emerald-600 shrink-0">{c.centrality}</span>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* FOOTER: educational tips */}
                <div className="xl:col-span-12">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="flex gap-3">
                          <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                            <Info className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">Como interpretar</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Quanto mais central e maiores os nós, maior a influência. Use para fortalecer relacionamentos.
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                            <Target className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">Identifique hubs</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Descubra quem são os principais conectores e influenciadores na sua rede.
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                            <Link2 className="h-5 w-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">Fortaleça laços</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Invista em conexões moderadas para transformá-las em apoiadores fortes.
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                            <Users className="h-5 w-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-900">Expanda sua rede</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Conecte grupos desconectados para aumentar o alcance da campanha.
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingLeaderId ? 'Editar Liderança' : 'Nova Liderança'}</DialogTitle>
              <DialogDescription>
                {editingLeaderId
                  ? 'Atualize os dados desta liderança. Mudanças são auditadas.'
                  : 'Cadastre uma nova liderança na rede'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="nome_completo">Nome Completo *</Label>
                  <Input
                    id="nome_completo"
                    value={formData.nome_completo}
                    onChange={(e) =>
                      setFormData({ ...formData, nome_completo: e.target.value })
                    }
                    required
                    data-testid="input-nome-completo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apelido">Apelido</Label>
                  <Input
                    id="apelido"
                    value={formData.apelido}
                    onChange={(e) => setFormData({ ...formData, apelido: e.target.value })}
                    placeholder="Como gosta de ser chamado(a)"
                    data-testid="input-apelido"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefone">Telefone (celular) *</Label>
                  <PhoneInput
                    id="telefone"
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    required
                    data-testid="input-telefone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_nascimento">Data de nascimento</Label>
                  <Input
                    id="data_nascimento"
                    type="date"
                    value={formData.data_nascimento}
                    onChange={(e) => setFormData({ ...formData, data_nascimento: e.target.value })}
                    data-testid="input-data-nascimento"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tipo">Tipo *</Label>
                  <Select
                    value={formData.tipo}
                    onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                  >
                    <SelectTrigger data-testid="select-tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposLideranca.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {dobradas.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="dobrada">Dobrada</Label>
                    <Select
                      value={formData.dobrada_id || 'principal'}
                      onValueChange={(value) =>
                        setFormData({ ...formData, dobrada_id: value === 'principal' ? '' : value })
                      }
                    >
                      <SelectTrigger data-testid="select-dobrada">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="principal">Candidato principal</SelectItem>
                        {dobradas.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="col-span-2">
                  <StateCitySelect
                    estado={formData.estado}
                    cidade={formData.cidade}
                    onEstadoChange={(v) => setFormData({ ...formData, estado: v, cidade: '' })}
                    onCidadeChange={(v) => setFormData({ ...formData, cidade: v })}
                    required
                    allowedStates={settings?.allowed_states}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="bairro">Bairro</Label>
                  <Input
                    id="bairro"
                    value={formData.bairro}
                    onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                    placeholder="Opcional"
                    data-testid="input-bairro"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="observacoes">Observações</Label>
                  <Input
                    id="observacoes"
                    value={formData.observacoes}
                    onChange={(e) =>
                      setFormData({ ...formData, observacoes: e.target.value })
                    }
                    data-testid="input-observacoes"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setEditingLeaderId(null); resetForm(); }}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="submit-lideranca-btn">
                  {editingLeaderId ? 'Salvar alterações' : 'Criar Liderança'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Details Dialog */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Detalhes da Liderança</DialogTitle>
            </DialogHeader>
            {selectedLideranca && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Nome</p>
                    <p className="font-medium">{selectedLideranca.nome_completo}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Apelido</p>
                    <p className="font-medium">{selectedLideranca.apelido ? `@${selectedLideranca.apelido}` : '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Telefone</p>
                    <p className="font-medium">{selectedLideranca.telefone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Tipo</p>
                    <p className="font-medium capitalize">{selectedLideranca.tipo}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Estado / Cidade</p>
                    <p className="font-medium">
                      {selectedLideranca.estado && `${selectedLideranca.estado} / `}
                      {selectedLideranca.cidade}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Bairro</p>
                    <p className="font-medium">{selectedLideranca.bairro}</p>
                  </div>
                </div>
                {selectedLideranca.stats && (
                  <div className="border-t pt-4">
                    <h3 className="font-bold mb-4">Estatísticas</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-primary">
                          {selectedLideranca.stats.total_acessos}
                        </p>
                        <p className="text-sm text-muted-foreground">Acessos</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-primary">
                          {selectedLideranca.stats.total_redirecionamentos}
                        </p>
                        <p className="text-sm text-muted-foreground">Redirects</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-primary">
                          {selectedLideranca.stats.taxa_conversao}%
                        </p>
                        <p className="text-sm text-muted-foreground">Taxa</p>
                      </div>
                    </div>
                  </div>
                )}
                {selectedLideranca.primary_link_token && (
                  <div className="border-t pt-4 space-y-2">
                    <h3 className="font-bold">Link de convite</h3>
                    <div className="flex gap-2">
                      <Input
                        value={buildInviteUrl(selectedLideranca.primary_link_token)}
                        readOnly
                        className="font-mono text-xs"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        onClick={() =>
                          handleCopyInvite({
                            primary_link_token: selectedLideranca.primary_link_token,
                          })
                        }
                        data-testid="details-copy-invite-btn"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* Invite Dialog */}
        <Dialog open={!!inviteLideranca} onOpenChange={(v) => !v && setInviteLideranca(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Link de convite</DialogTitle>
              <DialogDescription>
                Compartilhe este link/QR Code com pessoas que {inviteLideranca?.apelido || 'a liderança'} quer atrair como apoiadoras.
              </DialogDescription>
            </DialogHeader>
            {inviteLideranca && inviteUrl && (
              <div className="space-y-4">
                <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-5 text-center">
                  {inviteQr ? (
                    <img
                      src={inviteQr}
                      alt="QR Code"
                      className="w-48 h-48 mx-auto"
                      data-testid="invite-qr-code"
                    />
                  ) : (
                    <div className="w-48 h-48 mx-auto flex items-center justify-center text-slate-400">
                      Gerando QR Code...
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={downloadInviteQr}
                    disabled={!inviteQr}
                    data-testid="invite-download-qr-btn"
                  >
                    <Download className="h-4 w-4 mr-2" /> Baixar QR Code
                  </Button>
                </div>

                <div className="flex gap-2">
                  <Input value={inviteUrl} readOnly className="font-mono text-xs" />
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => handleCopyInvite(inviteLideranca)}
                    data-testid="invite-copy-btn"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>

                <Button
                  type="button"
                  className="w-full"
                  onClick={() => handleShareWhatsApp(inviteLideranca)}
                  data-testid="invite-share-whatsapp-btn"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Compartilhar no WhatsApp
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Leader Details Drawer — consolidates Performance / Links / Captação / Apoiadores */}
        <LeaderDetailsDrawer
          leader={drawerLeader}
          activeTenant={activeTenant}
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          initialTab={drawerInitialTab}
          onChange={fetchData}
          canEdit={canEditLiderancas}
        />
      </div>
    </Layout>
  );
};
